import { message } from 'antd';
import { useEffect, useState } from 'react';

import {
  EValidSSOKey,
  EQueryKeys,
  ESessionStorageKeys,
} from '../../../types';
import {
  sleep,
  retrieveSessionStorage,
  clearSessionStorage,
  isAllowedMcpRedirect,
} from '../utils/helpers';

export const useValidSSO = () => {
  const [isError, setError] = useState<Error | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const processValidSSO = async () => {
      try {
        setLoading(true);

        await sleep(1000);

        const authData = retrieveSessionStorage([
          ESessionStorageKeys.AUTH_TOKEN,
          ESessionStorageKeys.REFRESH_TOKEN,
          ESessionStorageKeys.ID_TOKEN,
          ESessionStorageKeys.ORG_ID,
          EValidSSOKey.VALID_SSO,
          EQueryKeys.REDIRECT_URI,
          EQueryKeys.APP_TYPE,
        ]);

        if (authData === null) {
          throw new Error('useValidSSO - Failed retrieving auth data from session storage');
        }

        const validSSO = authData[EValidSSOKey.VALID_SSO];

        if (validSSO === null) return;

        const isValidSSO: boolean = JSON.parse(validSSO);

        if (!isValidSSO) return;

        const appType = authData[EQueryKeys.APP_TYPE];

        const urlParams = new URLSearchParams(window.location.search);
        const urlRedirectURI = urlParams.get(EQueryKeys.REDIRECT_URI);

        let redirectURI: string | null = urlRedirectURI || authData[EQueryKeys.REDIRECT_URI];

        if (redirectURI === null) {
          message.error(
            "We've encountered an issue with your authentication data. Please try again later."
          );
          return;
        }

        const urlPrefixes = ['http://', 'https://'];
        const hasPrefix = urlPrefixes.some((prefix) => redirectURI!.includes(prefix));
        const sanitizedRedirectURI = hasPrefix
          ? redirectURI
          : redirectURI.includes('localhost')
            ? `http://${redirectURI}`
            : `https://${redirectURI}`;

        if (
          authData[ESessionStorageKeys.AUTH_TOKEN] === null ||
          authData[ESessionStorageKeys.REFRESH_TOKEN] === null ||
          authData[ESessionStorageKeys.ID_TOKEN] === null
        ) {
          message.error(
            "We've encountered an issue with your authentication. Please try again later."
          );
          return;
        }

        const authParams = `access=${authData[ESessionStorageKeys.AUTH_TOKEN]}&refresh=${authData[ESessionStorageKeys.REFRESH_TOKEN]}&id=${authData[ESessionStorageKeys.ID_TOKEN]}`;

        if (appType === 'mcp' && !isAllowedMcpRedirect(sanitizedRedirectURI)) {
          message.error(
            "We've encountered an issue with your authentication data. Please try again later."
          );
          return;
        }

        if (sanitizedRedirectURI && appType !== 'mobile') {
          const hasQueryParams = new URL(sanitizedRedirectURI).search !== '';

          clearSessionStorage([
            ESessionStorageKeys.AUTH_TOKEN,
            ESessionStorageKeys.REFRESH_TOKEN,
            ESessionStorageKeys.ID_TOKEN,
            ESessionStorageKeys.ORG_ID,
            EValidSSOKey.VALID_SSO,
            EQueryKeys.REDIRECT_URI,
            EQueryKeys.APP_TYPE,
          ]);

          window.location.replace(
            hasQueryParams
              ? `${sanitizedRedirectURI}&${authParams}`
              : `${sanitizedRedirectURI}?${authParams}`
          );
        }

        setLoading(false);
      } catch (err) {
        setError(err as Error);
        setLoading(false);
      }
    };

    processValidSSO();
  }, []);

  return {
    validSSOError: isError,
    validSSOLoading: isLoading,
  };
};
